import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconEmail, IconPencilSquare, IconQuestionMarkCircle } from '@gto/ui/assets'
import { Input } from '@gto/ui/input'
import { Skeleton } from '@gto/ui/skeleton'
import { Tooltip } from '@gto/ui/tooltip'
import { Typography } from '@gto/ui/typography'

import type { Account } from '@/api/gen'

import { ChangeEmailForm } from './change-email-form'

// Helper to check if verification email was sent within the last hour
const isWithinOneHour = (sentAt: string | null): boolean => {
  if (sentAt === null) {
    return false
  }

  const sentDate = new Date(sentAt)
  const now = new Date()
  const diffMs = now.getTime() - sentDate.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  return diffHours < 1
}

export type ChangeEmailWidgetProps = {
  isActive: boolean
  isPending: boolean
  accountData?: Account
  onOpen: () => void
  onClose: () => void
}

export const ChangeEmailWidget = ({
  isActive,
  isPending,
  accountData,
  onOpen,
  onClose,
}: ChangeEmailWidgetProps) => {
  // Auto-open if email not verified and verification email was sent within last hour
  const hasPendingEmailVerification =
    !isActive &&
    accountData !== undefined &&
    !accountData.email_verified &&
    accountData.verification_email_sent_at !== null &&
    isWithinOneHour(accountData.verification_email_sent_at)

  useEffect(
    function autoOpenPendingEmailVerification() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- async server-data arrival, not a user event: the account query resolving with an unconfirmed email sent < 1h ago auto-opens the change-email form. No interaction triggers it, so there is no handler home; onOpen toggles parent state (illegal to call during render), and the [hasPendingEmailVerification] dep fires the one-shot open once the condition first holds.
      if (hasPendingEmailVerification) {
        onOpen()
      }
    },
    [hasPendingEmailVerification, onOpen],
  )

  // Show form or verification pending when active
  if (isActive) {
    return (
      <ChangeEmailForm
        email={accountData?.email}
        onClose={onClose}
      />
    )
  }

  // Show email field when not active (view mode)
  return (
    <div>
      {isPending ? (
        <>
          <Skeleton
            variant="text"
            width="120px"
          />
          <Skeleton
            variant="text"
            width="150px"
          />
        </>
      ) : (
        <>
          <Typography
            gutterBottom
            className="px-2"
            color="text.secondary"
            component="p"
            variant="body2"
          >
            <FormattedMessage id="common.labels.email" />
          </Typography>
          <div className="flex items-center gap-3">
            <Input
              readOnly
              endAddon={<IconEmail />}
              value={accountData?.email}
            />
            <div className="flex items-center gap-1">
              <button
                className="flex cursor-pointer items-center border-none bg-transparent p-0 text-success-main enabled:hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={Boolean(accountData?.unconfirmed_email)}
                onClick={onOpen}
              >
                <IconPencilSquare />
              </button>
              {typeof accountData?.unconfirmed_email === 'string' &&
                accountData.unconfirmed_email.length > 0 && (
                  <Tooltip
                    content={<FormattedMessage id="user.settings.email.unconfrmed.tip" />}
                    placement="top"
                  >
                    <IconQuestionMarkCircle />
                  </Tooltip>
                )}
            </div>
          </div>

          {accountData?.unconfirmed_email !== undefined &&
            accountData.unconfirmed_email.length > 0 && (
              <Typography
                className="mt-2"
                color="text.secondary"
                variant="body2"
              >
                <FormattedMessage id="user.settings.email.unconfirmed" />{' '}
                <Typography
                  className="font-semibold"
                  color="text.primary"
                  component="span"
                >
                  {accountData.unconfirmed_email}
                </Typography>
              </Typography>
            )}
        </>
      )}
    </div>
  )
}
